<template>
    <div v-loading="isLoading">
        <el-row>
            <el-col :span="24">
                <el-alert
                        v-if="isInitial"
                        title="There is no setting saved in the database. Please init your personal settings."
                        type="info"
                        center
                        show-icon
                >
                </el-alert>
                <br/>
            </el-col>
        </el-row>
        <el-row>
            <el-col :span="18" :offset="3">
                <el-form
                        ref="form"
                        :model="form"
                        label-width="200px"
                        :rules="rules"
                >
                    <el-form-item
                            v-show="false"
                            label="ID"
                            prop="id"
                    >
                        <el-input v-model="form.id"></el-input>
                    </el-form-item>
                    <el-form-item
                            label="Branch Name"
                            prop="branch_name"
                    >
                        <el-input v-model="form.branch_name"></el-input>
                    </el-form-item>
                    <el-form-item
                            label="Name"
                            prop="name"
                    >
                        <el-input v-model="form.name"></el-input>
                    </el-form-item>
                    <el-form-item
                            label="Email"
                            prop="email"
                    >
                        <el-input v-model="form.email"></el-input>
                    </el-form-item>
                    <el-form-item
                            label="Mobile"
                            prop="mobile"
                    >
                        <el-input v-model="form.mobile"></el-input>
                    </el-form-item>
                    <el-form-item
                            label="Receive Notifications"
                            prop="notifiable"
                    >
                        <el-switch
                                v-model="form.notifiable"
                                active-color="#3f9eff"
                                inactive-color="#00000040">
                        </el-switch>
                    </el-form-item>
                    <el-form-item
                            label="Notification"
                            prop="notifiable_via"
                    >
                        <el-checkbox-group v-model="form.notifiable_via">
                            <el-checkbox label="sms">TXT</el-checkbox>
                            <el-checkbox label="mail">Email</el-checkbox>
                        </el-checkbox-group>
                    </el-form-item>
                    <el-form-item
                            label="Delay Threshold (minutes)"
                            prop="time_threshold"
                    >
                        <span slot="label">
                            Delay Threshold (minutes)
                            <el-tooltip class="item" effect="dark" content="Move the slider to change the length of delay before you will be notified." placement="bottom">
                              <i class="el-icon-question"></i>
                            </el-tooltip>
                        </span>
                        <el-slider
                                v-model="form.time_threshold"
                                :step="10"
                                show-stops
                        >
                        </el-slider>
                    </el-form-item>
                    <el-form-item style="margin-bottom: 0;">
                        <div style="line-height: normal;">
                            The branches below are your default branches, the Monitor page will show trucks arriving at these branches. TV Mode will ONLY show trucks arriving at these branches.
                        </div>
                    </el-form-item>
                    <el-form-item
                            label="All Branches"
                            prop="subscribe_all"
                    >
                        <span slot="label">
                            All Branches
                            <el-tooltip class="item" effect="dark" content="You will receive delay notifications for all scheduled trips. Switch All Branches off and select individual branches to only be notified for those branches." placement="bottom">
                              <i class="el-icon-question"></i>
                            </el-tooltip>
                        </span>
                        <el-switch
                                v-model="form.subscribe_all"
                                active-color="#3f9eff"
                                inactive-color="#00000040">
                        </el-switch>
                    </el-form-item>
                    <el-form-item v-show="! form.subscribe_all" style="margin-bottom: 0;">
                        <div style="line-height: normal;">
                            You will receive delay notifications for trucks arriving at these branches. Switch All Branches on to receive delay notifications for all branches.
                        </div>
                    </el-form-item>
                    <el-form-item
                            v-show="! form.subscribe_all"
                            label="Selected Branches"
                            prop="destination_place_ids">
                        <span slot="label">
                            Selected Branches
                            <el-tooltip class="item" effect="dark" content="You will receive notifications for all trucks arriving at these selected branches only." placement="bottom">
                              <i class="el-icon-question"></i>
                            </el-tooltip>
                        </span>
                        <el-select
                                v-model="form.destination_place_ids"
                                filterable
                                multiple
                        >
                            <el-option
                                    v-for="place in places"
                                    :label="place.name"
                                    :key="place.id"
                                    :value="place.id"
                            >
                                <span>{{place.name}}</span>
                                <span style="float:right;color:#ccc">{{place.address_readable}}</span>
                            </el-option>
                        </el-select>
                    </el-form-item>
                    <!-- Buttons -->
                    <el-form-item>
                        <el-button type="success" @click="handleSubmit('form')" :loading="isAddingSetting" v-if="isInitial">Init</el-button>
                        <el-button type="primary" @click="handleSubmit('form')" :loading="isUpdatingSetting" v-else>Save</el-button>
                        <el-button @click="handleReset('form')">Reset</el-button>
                    </el-form-item>
                </el-form>
            </el-col>
        </el-row>
    </div>
</template>

<script>
    export default {
        name: 'ScheduleMonitorSetting',
        props: {
            user: {
                required: true,
                type: Object,
            }
        },
        data () {
            let validateNotifiableVia = (rule, value, callback) => {
                if (this.form.notifiable && this.form.notifiable_via.length === 0) {
                    callback(new Error('Please select one notifiable method at least'));
                } else {
                    callback();
                }
            };
            let validateDestinationPlaceIds = (rule, value, callback) => {
                if (!this.form.subscribe_all && this.form.destination_place_ids.length === 0) {
                    callback(new Error('Please subscribe one place at least'));
                } else {
                    callback();
                }
            };
            return {
                rules: {
                    branch_name: [
                        { required: true, type: 'string', message: 'Please enter a branch name', trigger: 'blur' }
                    ],
                    name: [
                        { required: true, type: 'string', message: 'Please enter a name', trigger: 'blur' }
                    ],
                    email: [
                        { type: 'email', message: 'Please enter a validated email address', trigger: 'blur' }
                    ],
                    mobile: [
                        { type: 'string', message: 'Please enter a mobile number', trigger: 'blur' }
                    ],
                    notifiable_via: [
                        { validator: validateNotifiableVia, trigger: 'change'}
                    ],
                    destination_place_ids: [
                        { validator: validateDestinationPlaceIds, trigger: 'change'}
                    ],
                },
                form: {
                    id: '',
                    branch_name: '',
                    name: '',
                    email: '',
                    mobile: '',
                    notifiable: true,
                    notifiable_via: ['mail'],
                    time_threshold: 20,
                    subscribe_all: false,
                    destination_place_ids: [],
                }
            }
        },
        methods: {
            loadSetting () {
                this.$store.dispatch('loadTruckRunningSetting')
                    .then(res => {
                        //init the user setting, using user account details to fill some inputs
                        if (res.status === 204) {
                            this.form.branch_name = this.user.name;
                            this.form.name = this.user.name;
                            this.form.email = this.user.email;
                            this.form.mobile = this.user.mobile_number == null ? '' : this.user.mobile_number;
                        } else {
                            this.form = {...this.$store.getters.getTruckRunningSetting};
                        }
                    }).catch(err => {
                        this.$message.error('Sorry, failed to load your settings.');
                });
            },
            handleReset (name) {
                this.$refs[name].resetFields();
            },
            handleSubmit (name) {
                this.$refs[name].validate((valid) => {
                    if (valid) {
                        if (this.isInitial) {
                            //init: create a setting
                            this.$store.dispatch('addTruckRunningSetting', this.form)
                                .then(res => {
                                    this.$message.success('Saved settings successfully.');
                                    this.loadSetting();
                                }).catch(err => {
                                    this.$message.error('Sorry, failed to save the settings.');
                            });
                        } else {
                            //update a setting
                            this.$store.dispatch('updateTruckRunningSetting', this.form)
                                .then(res => {
                                    this.$message.success('Updated settings successfully.');
                                }).catch(err => {
                                    this.$message.error('Sorry, failed to update the settings.');
                            });
                        }
                    } else {
                        this.$message.error('Sorry, failed to validate the form.');
                    }
                })
            },
        },
        computed: {
            isInitial: function () {
                return this.form.id === '';
            },
            places: function () {
                return this.$store.getters.getPlaces;
            },
            isLoading: function () {
                return this.$store.getters.getTruckRunningSettingLoadStatus === 1;
            },
            isAddingSetting () {
                return this.$store.getters.getTruckRunningSettingAddStatus === 1;
            },
            isUpdatingSetting () {
                return this.$store.getters.getTruckRunningSettingUpdateStatus === 1;
            },
        },
        mounted() {
            this.loadSetting();
        }
    };
</script>

<style scoped>

</style>